import { Transition, TransitionService } from "@uirouter/angular";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, take, takeUntil, zip } from "rxjs";
import dayjs from "@gtmhub/dayjs";
import { CurrentUserService, IOnboardingMetadata, IOnboardingMetadataEntry } from "@gtmhub/users";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { fromTransitionHook } from "@webapp/core/routing/rxjs";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import {
  closeJourneyState,
  completeJourneyState,
  completeTransitionForState,
  createInitialOnboardingState,
  disabledJourneyState,
  getJourneyNextStep,
  getJourneyPreviousStep,
  journeyFirstStep,
  pendingStartJourneyState,
} from "@webapp/shared/components/onboarding/onboarding-state.util";
import { IOnboardingJourneyState, NEW_NAVIGATION_ONBOARDING_USER_SETTINGS_KEY } from "../../shared/components/onboarding/onboarding.models";
import {
  FAVORITES_STEP,
  INITIAL_STEP,
  NAVIGATION_COLOR_STEP,
  NEW_NAVIGATION_ONBOARDING_JOURNEY_MAX_VIEWS,
  NEW_NAVIGATION_ONBOARDING_JOURNEY_TRACK_EVENT,
  NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS,
  RECENTS_AND_SUGGESTED_STEP,
  REPORTS_STEP,
  SETTINGS_STEP,
  STILL_HAVE_QUESTIONS_STEP,
} from "../models/navigation-onboarding.models";
import { SettingsSubNavStateService } from "./settings/settings-sub-nav-state.service";
import { SettingsVisibilityService } from "./settings/settings-visibility.service";

/**
 * The NavigationOnboardingService will hold the state of the onboarding journey for the new navigation and
 * will persist it in the User's settings
 */

@Injectable({
  providedIn: "root",
})
export class NavigationOnboardingService {
  private navigationOnboardingMetadata: IOnboardingMetadataEntry;
  private navOnboardingState$: BehaviorSubject<IOnboardingJourneyState> = new BehaviorSubject<IOnboardingJourneyState>(createInitialOnboardingState());
  private markedForCompletion = false;
  private journeyTotalSteps = NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS;
  private skipSteps: number[] = [];

  constructor(
    private currentUserService: CurrentUserService,
    private featureTogglesFacade: FeatureTogglesFacade,
    transitionService: TransitionService,
    private destroyService$: DestroyService,
    private settingsVisibilityService: SettingsVisibilityService,
    private analyticsService: AnalyticsService,
    private settingsSubNavStateService: SettingsSubNavStateService
  ) {
    // setting default value of the onboarding metadata for the specific journey
    const onboardingMetaData = {
      [NEW_NAVIGATION_ONBOARDING_USER_SETTINGS_KEY]: { timesShown: 0, isCompleted: false, lastWatchedDate: null },
      ...this.currentUserService.getUserSetting<IOnboardingMetadata>("onboardingMetadata"),
    };
    this.navigationOnboardingMetadata = onboardingMetaData[NEW_NAVIGATION_ONBOARDING_USER_SETTINGS_KEY];
    zip([this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.NewExperienceOnboarding), this.settingsVisibilityService.showSettings$])
      .pipe(takeUntil(this.destroyService$))
      .subscribe(([newExperienceOnboarding, showSettings]) => {
        if (!this.shouldSeeJourneyAccordingToMetadata() || !newExperienceOnboarding) {
          this.updateState(disabledJourneyState());
        } else {
          if (!showSettings) {
            this.journeyTotalSteps--;
            this.skipSteps.push(SETTINGS_STEP);
          }
          this.updateState(pendingStartJourneyState(this.journeyTotalSteps, this.skipSteps));
        }
      });
    this.settingsSubNavStateService
      .firstVisibleState$()
      .pipe(take(1))
      .subscribe((settingsState) => {
        fromTransitionHook(transitionService, "onSuccess", {})
          .pipe(takeUntil(this.destroyService$))
          .subscribe((transition: Transition) => {
            if (this.navOnboardingState$.value.stage === "PENDING_START") return;
            const stateName = transition.targetState().name();
            const stepsStateMap = {
              [INITIAL_STEP]: "gtmhub.home.dashboard",
              [FAVORITES_STEP]: "gtmhub.home.dashboard",
              [RECENTS_AND_SUGGESTED_STEP]: "gtmhub.sessions",
              [REPORTS_STEP]: "gtmhub.lists",
              [SETTINGS_STEP]: settingsState,
              [NAVIGATION_COLOR_STEP]: "gtmhub.profileTheme",
              [STILL_HAVE_QUESTIONS_STEP]: "gtmhub.home.dashboard",
            };
            if (this.navOnboardingState$.value.stage === "IN_TRANSITION" && stateName === stepsStateMap[this.navOnboardingState$.value.currentStep]) {
              this.transitionCompleted();
            } else {
              this.closeJourney();
            }
          });
      });
  }

  private syncOnboardingMetadata(): Promise<unknown> {
    return this.currentUserService.setUserSetting({
      onboardingMetadata: {
        ...this.currentUserService.getUserSetting<IOnboardingMetadata>("onboardingMetadata"),
        [NEW_NAVIGATION_ONBOARDING_USER_SETTINGS_KEY]: this.navigationOnboardingMetadata,
      },
    }) as Promise<unknown>;
  }

  private updateState(state: IOnboardingJourneyState): void {
    if (this.markedForCompletion) {
      this.navOnboardingState$.next(completeJourneyState(state));
      this.markedForCompletion = false;
    } else {
      if (state.stage === "ENDED_BY_USER") {
        this.analyticsService.track(NEW_NAVIGATION_ONBOARDING_JOURNEY_TRACK_EVENT, { isCompleted: false, endedAtStep: state.currentStep });
      } else if ((state.stage === "IN_TRANSITION" || state.stage === "ONGOING") && state.currentStep !== this.navOnboardingState$.value.currentStep) {
        this.analyticsService.track(NEW_NAVIGATION_ONBOARDING_JOURNEY_TRACK_EVENT, {
          transitioning: true,
          toStep: state.currentStep,
          fromStep: this.navOnboardingState$.value.currentStep,
        });
      }
      this.navOnboardingState$.next(state);
    }
  }

  private shouldSeeJourneyAccordingToMetadata(): boolean {
    if (this.navigationOnboardingMetadata.isCompleted) return false;
    if (this.navigationOnboardingMetadata.timesShown >= NEW_NAVIGATION_ONBOARDING_JOURNEY_MAX_VIEWS) return false;
    if (dayjs().diff(this.navigationOnboardingMetadata.lastWatchedDate, "hours") <= 24) return false;
    return true;
  }

  public getState$(): Observable<IOnboardingJourneyState> {
    return this.navOnboardingState$.asObservable();
  }

  public startJourney(): void {
    this.updateState(journeyFirstStep(this.journeyTotalSteps, this.skipSteps));
  }

  public transitionCompleted(): void {
    this.updateState(completeTransitionForState(this.navOnboardingState$.value));
  }

  public moveForward(options: { inTransition: boolean; stepsDelta?: number }): void {
    this.updateState(getJourneyNextStep(this.navOnboardingState$.value, options));
  }

  public moveBackward(options: { inTransition: boolean; stepsDelta?: number }): void {
    this.updateState(getJourneyPreviousStep(this.navOnboardingState$.value, options));
  }

  public closeJourney(): void {
    this.updateState(closeJourneyState(this.navOnboardingState$.value));
  }

  public completeJourney(): void {
    if (this.navigationOnboardingMetadata.isCompleted === false) {
      this.analyticsService.track(NEW_NAVIGATION_ONBOARDING_JOURNEY_TRACK_EVENT, { isCompleted: true });
    }
    this.markedForCompletion = true;
    this.navigationOnboardingMetadata.isCompleted = true;
    this.syncOnboardingMetadata();
  }

  public markJourneyAsShown(): void {
    this.navigationOnboardingMetadata.timesShown++;
    this.navigationOnboardingMetadata.lastWatchedDate = dayjs().utc().toISOString();
    this.navigationOnboardingMetadata.isCompleted = this.navigationOnboardingMetadata.timesShown >= NEW_NAVIGATION_ONBOARDING_JOURNEY_MAX_VIEWS;
    this.syncOnboardingMetadata();
    this.analyticsService.track(NEW_NAVIGATION_ONBOARDING_JOURNEY_TRACK_EVENT, { shownToUser: true });
  }
}
